import { Configuration, LogLevel } from '@azure/msal-browser';

/**
 * MSAL configuration for the companion app
 * Uses the SAME app registration as the Impact Agent bot
 * Client ID: 52ca1551-12c0-4e02-b03f-a5a9a3f6ec9b
 * 
 * PHASED PERMISSION STRATEGY:
 * - BASE_SCOPES: Requested on first sign-in (user-consentable)
 * - EXTENDED_SCOPES: Requested via incremental consent when user enables features
 */

// Same client ID as the bot - shared app registration
export const CLIENT_ID = '52ca1551-12c0-4e02-b03f-a5a9a3f6ec9b';

export const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage', // More secure than localStorage
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error('[MSAL Error]', message);
            break;
          case LogLevel.Warning:
            console.warn('[MSAL Warn]', message);
            break;
          case LogLevel.Info:
            // Reduce noise in production
            if (process.env.NODE_ENV !== 'production') {
              console.info('[MSAL Info]', message);
            }
            break;
          case LogLevel.Verbose:
            // Only log verbose in development
            if (process.env.NODE_ENV !== 'production') {
              console.debug('[MSAL Verbose]', message);
            }
            break;
        }
      },
      logLevel: process.env.NODE_ENV === 'production' ? LogLevel.Warning : LogLevel.Verbose,
    },
  },
};

/**
 * Microsoft Graph API scopes - organized by consent requirement
 */
export const graphScopes = {
  // ========================================
  // OPENID CONNECT SCOPES
  // ========================================
  openid: 'openid',
  profile: 'profile',
  email: 'email',
  offlineAccess: 'offline_access',

  // ========================================
  // BASE SCOPES (user-consentable, requested on first sign-in)
  // ========================================
  /** Basic user profile */
  userRead: 'User.Read',
  /** Read user's files (reduced from Files.ReadWrite.All) */
  filesRead: 'Files.Read',
  /** Read and write to user's OneDrive (for config storage) */
  filesReadWrite: 'Files.ReadWrite',
  /** Read joined Teams basic info */
  teamReadBasic: 'Team.ReadBasic.All',
  /** Read team channels basic info */
  channelReadBasic: 'Channel.ReadBasic.All',
  /** Read relevant people (for suggested colleagues) */
  peopleRead: 'People.Read',
  /** Enumerate chat threads and participants */
  chatReadBasic: 'Chat.ReadBasic',
  /** Read chat messages (user-consentable in most tenants) */
  chatMessageRead: 'ChatMessage.Read',

  // ========================================
  // EXTENDED SCOPES (may require admin consent in many tenants)
  // These are requested only when user enables Email/Calendar features
  // ========================================
  /** Read user's email - may require admin consent */
  mailRead: 'Mail.Read',
  /** Read email metadata only (lighter alternative) */
  mailReadBasic: 'Mail.ReadBasic',
  /** Read user's calendar - may require admin consent */
  calendarsRead: 'Calendars.Read',

  // ========================================
  // FALLBACK SCOPES (used if primary scopes fail)
  // ========================================
  /** Full chat read - fallback if ChatMessage.Read fails */
  chatRead: 'Chat.Read',
  /** Read basic user properties in directory - for people picker */
  userReadBasicAll: 'User.ReadBasic.All',
  /** Read channel messages - requires special consent */
  channelMessageRead: 'ChannelMessage.Read.All',
};

/**
 * BASE_SCOPES - Requested on first sign-in
 * 
 * These are user-consentable scopes that allow the agent to function
 * with core features (Teams chats + files) without admin approval.
 */
export const BASE_SCOPES = [
  graphScopes.openid,
  graphScopes.profile,
  graphScopes.email,
  graphScopes.offlineAccess,
  graphScopes.userRead,
  graphScopes.filesRead,
  graphScopes.channelReadBasic,
  graphScopes.teamReadBasic,
  graphScopes.peopleRead,
  graphScopes.chatReadBasic,
  graphScopes.chatMessageRead,
];

/**
 * EXTENDED_SCOPES_EMAIL - Requested when user enables Email/Calendar
 * 
 * These may require admin consent in many tenants.
 */
export const EXTENDED_SCOPES_EMAIL = [
  graphScopes.mailRead,
  graphScopes.calendarsRead,
];

/**
 * EXTENDED_SCOPES_MAIL_BASIC - Lighter email permission
 */
export const EXTENDED_SCOPES_MAIL_BASIC = [
  graphScopes.mailReadBasic,
];

/**
 * Login request configuration - uses BASE_SCOPES only
 */
export const loginRequest = {
  scopes: BASE_SCOPES,
  prompt: 'select_account', // Force account picker for multi-tenant
  authority: 'https://login.microsoftonline.com/common',
};

/**
 * All scopes the app may ever request (for token acquisition)
 */
export const allScopes = [
  ...BASE_SCOPES,
  ...EXTENDED_SCOPES_EMAIL,
  graphScopes.filesReadWrite, // For config storage
  graphScopes.userReadBasicAll,
  graphScopes.chatRead,
  graphScopes.channelMessageRead,
];

/**
 * Build admin consent URL for a tenant
 * @param tenantId - The tenant ID (use 'common' if unknown)
 * @param redirectUri - Where to redirect after consent
 * @param scopes - Optional specific scopes to request
 */
export function buildAdminConsentUrl(
  tenantId: string = 'common',
  redirectUri: string = window.location.origin,
  scopes?: string[]
): string {
  const baseUrl = `https://login.microsoftonline.com/${tenantId}/adminconsent`;
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    redirect_uri: redirectUri,
  });
  
  if (scopes && scopes.length > 0) {
    params.set('scope', scopes.join(' '));
  }
  
  return `${baseUrl}?${params.toString()}`;
}

/**
 * Check if an error indicates admin consent is required
 */
export function isAdminConsentRequired(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  
  const err = error as { errorCode?: string; errorMessage?: string; message?: string };
  const errorCode = err.errorCode || '';
  const errorMessage = (err.errorMessage || err.message || '').toLowerCase();
  
  return (
    errorCode === 'consent_required' ||
    errorCode === 'interaction_required' ||
    errorMessage.includes('admin') ||
    errorMessage.includes('consent') ||
    errorMessage.includes('approval') ||
    errorMessage.includes('aadsts65001') || // consent required
    errorMessage.includes('aadsts50076') || // MFA required (often related)
    errorMessage.includes('aadsts90094')    // admin consent required
  );
}

/**
 * Extract specific error code from MSAL error
 */
export function extractErrorCode(error: unknown): string {
  if (!error || typeof error !== 'object') return 'unknown_error';
  
  const err = error as { 
    errorCode?: string; 
    subError?: string;
    errorMessage?: string;
  };
  
  if (err.subError) return err.subError;
  if (err.errorCode) return err.errorCode;
  
  const message = err.errorMessage || '';
  const match = message.match(/AADSTS\d+/i);
  return match ? match[0].toLowerCase() : 'unknown_error';
}
